import { addDays, differenceInCalendarDays, isAfter, isBefore, startOfDay } from "date-fns";

export const BOOKING_RULES = {
  MIN_DAYS: 1,
  MAX_DAYS: 999,
  ADVANCE_DAYS: 999,
} as const;

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export function validateBookingDates(dateFrom: string, dateTo: string): ValidationResult {
  const from = startOfDay(new Date(dateFrom));
  const to = startOfDay(new Date(dateTo));
  const today = startOfDay(new Date());
  const maxDate = addDays(new Date(), BOOKING_RULES.ADVANCE_DAYS);

  if (isNaN(from.getTime()) || isNaN(to.getTime())) {
    return { valid: false, error: "Neteisingos datos" };
  }
  if (isBefore(from, today)) {
    return { valid: false, error: "Nuoma galima nuo šiandien" };
  }
  if (isAfter(from, maxDate)) {
    return { valid: false, error: `Galima rezervuoti ne daugiau nei ${BOOKING_RULES.ADVANCE_DAYS} dienų į priekį` };
  }
  if (isAfter(from, to)) {
    return { valid: false, error: "Pradžios data negali būti vėlesnė nei pabaigos" };
  }
  const days = differenceInCalendarDays(to, from) + 1;
  if (days > BOOKING_RULES.MAX_DAYS) {
    return { valid: false, error: `Maksimali nuomos trukmė — ${BOOKING_RULES.MAX_DAYS} dienų` };
  }
  return { valid: true };
}

/**
 * The latest return date selectable for a pickup on `pickup`, given the days
 * already booked. A rental must occupy ONE contiguous free range: once a booked
 * day falls after the pickup date, no return date on or after it is valid (the
 * range would straddle the booked period). Returns `maxDate` when no booked day
 * follows the pickup. `bookedDays` may be in any order.
 */
export function latestReturnDate(pickup: Date, bookedDays: Date[], maxDate: Date): Date {
  const pickupStart = startOfDay(pickup);
  let firstBookedAfter: Date | undefined;
  for (const d of bookedDays) {
    const day = startOfDay(d);
    if (isAfter(day, pickupStart) && (!firstBookedAfter || isBefore(day, firstBookedAfter))) {
      firstBookedAfter = day;
    }
  }
  if (!firstBookedAfter) return maxDate;
  const cap = addDays(firstBookedAfter, -1);
  return isBefore(cap, maxDate) ? cap : maxDate;
}

export function validateBookingForm(data: {
  client_name: string;
  client_phone: string;
  client_email: string;
}): ValidationResult {
  if (!data.client_name || data.client_name.trim().length < 2) {
    return { valid: false, error: "Įveskite vardą ir pavardę" };
  }
  if (!data.client_phone || data.client_phone.trim().length < 6) {
    return { valid: false, error: "Įveskite telefono numerį" };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!data.client_email || !emailRegex.test(data.client_email)) {
    return { valid: false, error: "Įveskite teisingą el. pašto adresą" };
  }
  return { valid: true };
}
